import React, { useRef, useState } from 'react';
import {
    Box, Typography, Button, Alert, Chip, Stack, CircularProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { dashboardPaperSx } from '../DashboardPanel';

export const ACCEPT = '.pdf,.csv,.docx,.pptx,.xlsx,.txt,.html,.md';

const FORMAT_CHIPS = ['PDF', 'CSV', 'DOCX', 'PPTX', 'XLSX', 'TXT', 'HTML', 'MD'];

const AsistentUploadZone = ({
    t,
    theme,
    uploading,
    uploadStatus,
    onUpload,
}) => {
    const fileInputRef = useRef(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);

    const handleFile = (file) => {
        if (file) setUploadFile(file);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFile(e.dataTransfer.files?.[0] || null);
    };

    const handleUploadClick = () => {
        if (!uploadFile) return;
        onUpload(uploadFile, () => {
            setUploadFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        });
    };

    return (
        <Box sx={{ ...dashboardPaperSx(theme), p: 2.5, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {t('pages.asistent.uploadTitle')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('pages.asistent.uploadHint')}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                {FORMAT_CHIPS.map((fmt) => (
                    <Chip key={fmt} label={fmt} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem' }} />
                ))}
            </Stack>

            <Box
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                    border: '2px dashed',
                    borderColor: dragOver ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: dragOver ? 'action.selected' : 'action.hover',
                    transition: 'all 0.2s ease',
                    '&:hover': { borderColor: 'primary.light', bgcolor: 'action.selected' },
                }}
            >
                <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1, opacity: 0.85 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t('pages.asistent.dragDropHint')}
                </Typography>
                {uploadFile && (
                    <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>
                        {uploadFile.name}
                    </Typography>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept={ACCEPT}
                    onChange={(e) => handleFile(e.target.files?.[0] || null)}
                />
            </Box>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                <Button
                    variant="outlined"
                    component="label"
                    disabled={uploading}
                    size="small"
                >
                    {t('pages.asistent.selectFile')}
                    <input
                        type="file"
                        hidden
                        accept={ACCEPT}
                        onChange={(e) => handleFile(e.target.files?.[0] || null)}
                    />
                </Button>
                <Button
                    variant="contained"
                    startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
                    onClick={handleUploadClick}
                    disabled={uploading || !uploadFile}
                    size="small"
                >
                    {t('pages.asistent.uploadButton')}
                </Button>
            </Stack>

            {uploadStatus && (
                <Alert severity={uploadStatus.type} sx={{ mt: 2 }}>
                    {uploadStatus.text}
                </Alert>
            )}
        </Box>
    );
};

export default AsistentUploadZone;
